import { TokenManager } from '../auth/TokenManager';
import {
  clearRecalculationPending,
  emitWorkbookCompanyEvent,
  markRecalculationPending,
  mayRecalculateFor,
  onOfficeReadyLate,
  recalculationPendingInFile,
  workbookCompany,
} from './workbookCompany';
import type { RecalculationCause, WorkbookCompany } from './workbookCompany';
import { allCellsSetOff, allCellsSetOffSince, paneActionRunning, sheetCellsSetOffSince } from './paneActivity';

/**
 * Recalculation that stays inside this workbook. Excel's application-wide calculation recalculates
 * every open workbook, and on desktop that reaches workbooks bound to other companies: their saved
 * figures would turn into errors the moment the add-in is on another company. The add-in marks every
 * formula on each sheet of this workbook dirty and recalculates it, which reaches this workbook and
 * nothing else, cells that reach Bifrost through a defined name included.
 *
 * A Bifrost cell's figure arrives after the calculation that asked for it. In automatic calculation
 * Excel then recalculates what reads it by itself; in manual calculation that is left to F9 in Excel,
 * and the pane's Recalculate says so.
 */

/** When the recalculation after a cell bound the workbook is first tried, and how long it waits after each failure. */
const RECALCULATE_AFTER_BINDING_MS = 1500;
const RETRY_AFTER_BINDING_MS = [5000, 15000];
/** How long that recalculation waits again while a pane action runs. */
const PANE_BUSY_RETRY_MS = 1500;
/** A recalculation that failed is tried again on a sheet's activation at most this often. */
const ACTIVATION_RETRY_MS = 60_000;
/**
 * How long before a binding a recalculation that set the cells off still counts as the one whose
 * requests bound the workbook: they go out a batching window after the cells ask, later on a large
 * workbook.
 */
const SET_OFF_BEFORE_BINDING_MS = 60_000;
/** How long to wait for the cells' figures, and how long with no request in flight counts as all in. */
const SETTLE_LIMIT_MS = 5 * 60 * 1000;
const SETTLE_QUIET_MS = 800;
/** Excel may take a moment to start calling the functions; until a request is seen, quiet means not started. */
const SETTLE_QUIET_BEFORE_ANY_MS = 3000;
const SETTLE_TICK_MS = 200;

interface CellRequests {
  pending: Map<string, unknown>;
  pendingRows?: Map<string, unknown>;
  started?: number;
}

/** The cells' requests, LEDGER's and ENTRIES', which the functions bundle keeps on globalThis. */
function cellRequests(): { inFlight: number; started: number } {
  const state = (globalThis as unknown as { __bifrostPreCalc?: CellRequests }).__bifrostPreCalc;
  return {
    inFlight: (state?.pending.size ?? 0) + (state?.pendingRows?.size ?? 0),
    started: state?.started ?? 0,
  };
}

/** How many requests the cells have started so far; what `waitForCells` counts from. */
export function cellRequestsStarted(): number {
  return cellRequests().started;
}

/**
 * Resolves true once no cell has had a request in flight for a little while, false at the limit.
 * Reports requests done out of those started since `baseline` — Excel calls the functions in waves,
 * so "waiting" alone would swing up and down.
 */
export function waitForCells(
  onProgress: (done: number, total: number) => void = () => undefined,
  baseline: number = cellRequestsStarted(),
): Promise<boolean> {
  const startedAt = Date.now();
  let quietSince = 0;
  return new Promise((resolve) => {
    const tick = (): void => {
      const { inFlight, started } = cellRequests();
      const total = started - baseline;
      onProgress(total - inFlight, total);
      if (inFlight > 0) quietSince = 0;
      else if (!quietSince) quietSince = Date.now();
      if (quietSince && Date.now() - quietSince >= (total > 0 ? SETTLE_QUIET_MS : SETTLE_QUIET_BEFORE_ANY_MS)) {
        resolve(true);
        return;
      }
      if (Date.now() - startedAt >= SETTLE_LIMIT_MS) {
        resolve(false);
        return;
      }
      setTimeout(tick, SETTLE_TICK_MS);
    };
    setTimeout(tick, SETTLE_TICK_MS);
  });
}

/** Marks every formula dirty on each sheet and recalculates it. */
export function calculateSheets(sheets: Excel.Worksheet[]): void {
  sheets.forEach(sheet => sheet.calculate(true));
}

/** The application with its calculation mode loaded at the next sync; null where the API cannot read it. */
function withCalculationMode(context: Excel.RequestContext): Excel.Application | null {
  if (!Office.context?.requirements?.isSetSupported?.('ExcelApi', '1.8')) return null;
  return context.workbook.application.load('calculationMode');
}

function calculatesManually(application: Excel.Application | null): boolean {
  return application !== null && String(application.calculationMode) === 'Manual';
}

/** Queues a read of the calculation mode in `context`; what it answers holds after the next sync. */
export function manualCalculationOf(context: Excel.RequestContext): () => boolean {
  const application = withCalculationMode(context);
  return () => calculatesManually(application);
}

/** Added to the pane's Recalculate result when Excel calculates manually. */
export const MANUAL_CALCULATION_HINT = 'Calculation is set to manual in Excel. Press F9 to update formulas that use these figures.';

/** Converting every formula straight after fetching fresh figures, in manual calculation. */
export const MANUAL_CONVERT_MESSAGE = 'Calculation is set to manual in Excel, so nothing was converted: formulas that use '
  + 'the fetched figures update only with F9. Press F9, then convert with "Fetch fresh values first" unticked.';

/** `message`, with the hint where Excel calculates manually. */
export function withManualHint(message: string, manual: boolean): string {
  return manual ? `${message} ${MANUAL_CALCULATION_HINT}` : message;
}

/** Whether Excel calculates manually; false where the API cannot tell. */
async function calculatesManuallyNow(): Promise<boolean> {
  let manual = (): boolean => false;
  await Excel.run(async (context) => {
    manual = manualCalculationOf(context);
    await context.sync();
  });
  return manual();
}

/**
 * Why converting every formula after fetching fresh figures would freeze stale results, or null. In
 * manual calculation, what reads the refreshed cells updates only with F9, so a total would be frozen
 * before it does. Converting only Bifrost formulas is not affected.
 */
export async function manualConversionRefusal(allFormulas: boolean, fetchesFirst: boolean): Promise<string | null> {
  if (!allFormulas || !fetchesFirst) return null;
  return (await calculatesManuallyNow()) ? MANUAL_CONVERT_MESSAGE : null;
}

/**
 * Marks every formula dirty on each sheet of this workbook, but those `skip` names, and recalculates
 * it, one sheet to a request, so no request grows with the workbook.
 */
async function calculateEverySheet(skip: (sheetId: string) => boolean = () => false): Promise<void> {
  await Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    worksheets.load('items/id');
    await context.sync();
    await worksheets.items
      .filter(sheet => !skip(sheet.id))
      .reduce<Promise<unknown>>((previous, sheet) => previous.then(() => {
        sheet.calculate(true);
        return context.sync();
      }), Promise.resolve());
  });
}

/**
 * Every Bifrost cell of this workbook has just been set off: the recalculation the file owed has run,
 * and one after a binding that is still waiting, held or failed is done with.
 */
export function everyCellSetOff(): void {
  allCellsSetOff();
  const after = afterBinding();
  if (after.state !== 'waiting' && after.state !== 'held' && after.state !== 'failed') {
    clearRecalculationPending();
    return;
  }
  if (after.timer) clearTimeout(after.timer);
  after.timer = undefined;
  finished();
}

/** Recalculates every sheet of this workbook and no other workbook. Resolves once the cells are set off. */
export async function recalculateThisWorkbook(): Promise<void> {
  await calculateEverySheet();
  everyCellSetOff();
}

interface AfterBinding {
  /** `held`: the add-in was on another company than the workbook's when it was due to run. */
  state?: 'waiting' | 'running' | 'held' | 'failed' | 'done';
  /** What left the cells to be recalculated: a cell's binding, or a move the file recorded. */
  cause?: RecalculationCause;
  /** Owed since an earlier session, as the file recorded it: the line says so until it has run. */
  resumed?: boolean;
  failures: number;
  /** When the cell's request bound the workbook. */
  boundAt?: number;
  /** When the last attempt failed for good. */
  failedAt?: number;
  /** When a sheet's activation last tried again. */
  activationRetryAt?: number;
  reported?: boolean;
  timer?: ReturnType<typeof setTimeout>;
}

interface MovePending {
  company: WorkbookCompany;
  /** When the workbook moved; every cell set off since then finishes the move. */
  since: number;
  /** Its recalculation failed or was held back; until then the move is under way, not unfinished. */
  failed?: boolean;
  /** When a sheet's activation last tried to finish it. */
  triedAt?: number;
}

interface RecalcHolder {
  __bifrostAfterBinding?: AfterBinding;
  __bifrostMovePending?: MovePending;
  /** This session moved or bound the workbook, or resumed what the file owed: it knows better than the file. */
  __bifrostRecalcThisSession?: boolean;
}

/** On globalThis because the functions bundle and the task-pane bundle each load their own copy. */
const holder = (): RecalcHolder => globalThis as unknown as RecalcHolder;

let tokenManager: TokenManager | null = null;

/** The company of the stored session; null when there is none or it cannot be read. */
async function signedInCompanyId(): Promise<string | null> {
  if (!tokenManager) tokenManager = new TokenManager();
  try {
    return (await tokenManager.getUserInfo())?.companyId ?? null;
  } catch {
    return null;
  }
}

function afterBinding(): AfterBinding {
  const state = holder();
  if (!state.__bifrostAfterBinding) state.__bifrostAfterBinding = { failures: 0 };
  return state.__bifrostAfterBinding;
}

function schedule(cause: RecalculationCause): void {
  holder().__bifrostRecalcThisSession = true;
  const after = afterBinding();
  after.boundAt = Date.now();
  after.cause = cause;
  tryAfter(RECALCULATE_AFTER_BINDING_MS);
}

/**
 * After a cell's request bound this workbook: recalculate it, once this session, so no cell keeps a
 * figure saved for another company beside the ones just computed. Recorded in the file until it has
 * run. Deferred, since a custom function must not recalculate the workbook it is being calculated in,
 * and tried again when Excel refuses, as it does while a cell is being edited.
 */
export function recalculateAfterBinding(): void {
  const after = afterBinding();
  if (after.state) return;
  const bound = workbookCompany();
  if (bound) markRecalculationPending({ companyId: bound.companyId, cause: 'binding' });
  schedule('binding');
}

/**
 * A recalculation the file says an earlier session left owed, after a move or a binding: run now,
 * with the same retries, once this session. The caller checks that it computes the cells for the
 * company the workbook belongs to.
 */
export function resumeUnfinishedRecalculation(): void {
  if (holder().__bifrostRecalcThisSession) return;
  const after = afterBinding();
  const pending = recalculationPendingInFile();
  if (!pending) return;
  after.resumed = true;
  schedule(pending.cause);
}

/**
 * Tries a recalculation after binding that failed again now, as on a sheet's activation, when the
 * user is not in the middle of editing a cell: at most once a minute.
 */
export function retryRecalculationAfterBinding(): void {
  const after = afterBinding();
  if (after.failures === 0 || (after.state !== 'waiting' && after.state !== 'failed')) return;
  const now = Date.now();
  if (after.activationRetryAt !== undefined && now - after.activationRetryAt < ACTIVATION_RETRY_MS) return;
  after.activationRetryAt = now;
  if (after.timer) clearTimeout(after.timer);
  after.timer = undefined;
  void recalculateNowAfterBinding();
}

/**
 * A recalculation after binding held while the add-in was on another company: tried again, now that
 * the caller has seen it on the workbook's company.
 */
export function rearmRecalculationAfterBinding(): void {
  if (afterBinding().state === 'held') tryAfter(RECALCULATE_AFTER_BINDING_MS);
}

/** Whether that recalculation gave up, and nothing has asked every cell again since. */
export function recalculationAfterBindingFailed(): boolean {
  const after = afterBinding();
  return after.state === 'failed' && !allCellsSetOffSince(after.failedAt ?? 0);
}

function tryAfter(ms: number): void {
  const after = afterBinding();
  after.state = 'waiting';
  after.timer = setTimeout(() => {
    after.timer = undefined;
    void recalculateNowAfterBinding();
  }, ms);
}

async function recalculateNowAfterBinding(): Promise<void> {
  const after = afterBinding();
  if (after.state === 'running' || after.state === 'done') return;
  // A pane action must not be cut across, nor a conversion to values read #BUSY! from its cells.
  if (paneActionRunning()) {
    tryAfter(PANE_BUSY_RETRY_MS);
    return;
  }
  after.state = 'running';
  const companyId = await signedInCompanyId();
  if (paneActionRunning()) {
    tryAfter(PANE_BUSY_RETRY_MS);
    return;
  }
  // For another company than the workbook's, every cell would only turn into an error, and the file
  // would count the recalculation as done. Held, with no failure counted and the file still owing it,
  // until the add-in is seen on the workbook's company again.
  if (!mayRecalculateFor(companyId)) {
    after.state = 'held';
    return;
  }
  const since = (after.boundAt ?? 0) - SET_OFF_BEFORE_BINDING_MS;
  // Every cell was asked again around the binding: by the recalculation whose requests bound it, or
  // by the pane since.
  if (allCellsSetOffSince(since)) {
    finished();
    return;
  }
  try {
    let skipped = false;
    await calculateEverySheet((sheetId) => {
      const asked = sheetCellsSetOffSince(sheetId, since);
      skipped = skipped || asked;
      return asked;
    });
    if (!skipped) allCellsSetOff();
    finished();
  } catch (error) {
    after.failures++;
    console.warn('Could not recalculate the workbook after binding it:', error);
    const wait = RETRY_AFTER_BINDING_MS[after.failures - 1];
    if (wait !== undefined) {
      tryAfter(wait);
      return;
    }
    after.state = 'failed';
    after.failedAt = Date.now();
    if (after.reported) return;
    after.reported = true;
    // Said on the pane's line, the next time it is seen; the pane is not opened for it.
    emitWorkbookCompanyEvent({ kind: 'recalculateFailed' });
  }
}

/** Every cell is recalculated for the workbook's company: nothing is owed, and the line may say so. */
function finished(): void {
  const after = afterBinding();
  const said = after.failures > 0 || after.resumed === true;
  after.state = 'done';
  clearRecalculationPending();
  if (said) emitWorkbookCompanyEvent({ kind: 'changed' });
}

/**
 * A company chosen in the pane: the workbook now belongs to it, and its cells are still to be
 * recalculated, which the file records until they are.
 */
export function moveStarted(company: WorkbookCompany): void {
  holder().__bifrostRecalcThisSession = true;
  holder().__bifrostMovePending = { company, since: Date.now() };
  markRecalculationPending({ companyId: company.companyId, cause: 'move' });
}

/** The move's recalculation failed or was held back. */
export function moveFailed(): void {
  const pending = holder().__bifrostMovePending;
  if (pending) pending.failed = true;
}

/**
 * The company a move is unfinished for: the workbook was moved to it, recalculating failed or was held
 * back, and its cells were not all set off since. Null while that recalculation is still under way,
 * and once they were.
 */
export function unfinishedMove(): WorkbookCompany | null {
  const pending = holder().__bifrostMovePending;
  if (!pending) return null;
  if (allCellsSetOffSince(pending.since)) {
    holder().__bifrostMovePending = undefined;
    return null;
  }
  return pending.failed ? pending.company : null;
}

/**
 * What the workbook's line says the cells still owe: a move or a binding whose recalculation failed or
 * was held back this session, or one an earlier session left owed, as the file records it, until every
 * cell was set off since. Nothing while this session's own recalculation is still under way.
 */
export function owedRecalculation(): RecalculationCause | null {
  if (unfinishedMove()) return 'move';
  const after = afterBinding();
  const resumedOwed = after.resumed === true && after.state !== 'done' && !allCellsSetOffSince(after.boundAt ?? 0);
  if (recalculationAfterBindingFailed() || after.state === 'held' || resumedOwed) return after.cause ?? 'binding';
  if (holder().__bifrostRecalcThisSession) return null;
  return recalculationPendingInFile()?.cause ?? null;
}

/**
 * Tries to finish an unfinished move, as on a sheet's activation: at most once a minute, not while a
 * pane action runs, and only while the workbook still belongs to the company it was moved to and the
 * add-in is on it. `recalculate` is the pane's. Answers whether it tried.
 */
export async function retryUnfinishedMove(currentCompanyId: string | null, recalculate: () => Promise<void>): Promise<boolean> {
  const company = unfinishedMove();
  const pending = holder().__bifrostMovePending;
  if (!company || !pending || paneActionRunning()) return false;
  const now = Date.now();
  if (pending.triedAt !== undefined && now - pending.triedAt < ACTIVATION_RETRY_MS) return false;
  if (workbookCompany()?.companyId !== company.companyId || !mayRecalculateFor(currentCompanyId)) return false;
  pending.triedAt = now;
  try {
    await recalculate();
  } catch (error) {
    console.warn('Could not finish moving the workbook:', error);
  }
  return true;
}

/**
 * Cells that stopped waiting for Office answered "not read yet". Once Office initialises after all,
 * this workbook is recalculated once, so they recover without anyone recalculating — where
 * `mayRecalculate` says that computes them for the company the workbook belongs to.
 */
export function recalculateWhenOfficeReadyLate(mayRecalculate: () => Promise<boolean>): void {
  onOfficeReadyLate(() => {
    setTimeout(() => {
      mayRecalculate()
        .then(may => (may ? recalculateThisWorkbook() : undefined))
        .catch((error: unknown) => console.warn('Could not recalculate the workbook once Office was ready:', error));
    }, 0);
  });
}
